import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, Star } from "lucide-react";
import { api, formatApiError } from "../../booking/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function RegularsPage() {
  const [data, setData] = useState(null);
  const [minVisits, setMinVisits] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/api/admin/regulars").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => (data?.regulars || []).filter((g) => g.visits >= minVisits), [data, minVisits]);
  const withPhone = rows.filter((g) => g.whatsapp_ok);
  useEffect(() => { setSelected(new Set()); }, [minVisits]);
  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allOn = withPhone.length > 0 && withPhone.every((g) => selected.has(g.key));

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/api/admin/regulars/invite", { keys: [...selected] });
      toast.success(`Invites: ${r.data.sent} sent · ${r.data.skipped} skipped · ${r.data.failed} failed`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
      setConfirm(false);
    }
  };

  const p = data?.priority;
  return (
    <div data-testid="admin-regulars-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Star size={20} className="fill-amber-400 text-amber-400" /> Regulars</h1>
        <Button onClick={() => setConfirm(true)} disabled={!selected.size} data-testid="regulars-send-btn">
          <Send size={15} className="mr-2" /> Send invites ({selected.size})
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mt-2" data-testid="regulars-priority-info">
        {p?.start && p?.end
          ? `Early access window: ${p.start} → ${p.end}${p.active ? " (active now — only returning guests can book)" : ""}. Change it in Settings.`
          : "No early-access window set — invites still work; set dates in Settings to give regulars priority booking."}
      </p>
      <div className="flex items-center gap-3 mt-4 text-sm">
        <label htmlFor="min-visits">Min. visits</label>
        <Input id="min-visits" type="number" min={1} value={minVisits} onChange={(e) => setMinVisits(Math.max(1, Number(e.target.value)))}
          className="w-20" data-testid="regulars-min-visits" />
        <span className="text-muted-foreground">{rows.length} guests · {withPhone.length} with WhatsApp number</span>
      </div>
      <div className="bg-white rounded-lg border mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allOn} data-testid="regulars-select-all"
                  onCheckedChange={(v) => setSelected(v ? new Set(withPhone.map((g) => g.key)) : new Set())} />
              </TableHead>
              <TableHead>Guest</TableHead><TableHead>Visits</TableHead><TableHead>Last visit</TableHead>
              <TableHead>Language</TableHead><TableHead>Invite</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((g) => (
              <TableRow key={g.key} data-testid={`regular-row-${g.key}`}>
                <TableCell>
                  <Checkbox checked={selected.has(g.key)} disabled={!g.whatsapp_ok} onCheckedChange={() => toggle(g.key)} data-testid={`regular-check-${g.key}`} />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{g.name}</div>
                  <div className="text-xs text-muted-foreground">{g.phone || "no phone"}{g.phone && !g.whatsapp_ok ? " (invalid)" : ""} · {g.email || "no email"}</div>
                </TableCell>
                <TableCell>{g.visits}</TableCell>
                <TableCell>{g.last_visit}</TableCell>
                <TableCell className="uppercase text-xs">{g.language}</TableCell>
                <TableCell className="text-xs">
                  {g.invited_at ? `${g.invite_status} · ${String(g.invited_at).slice(0, 10)}` : "—"}
                  {g.code && <div className="font-mono text-muted-foreground">{g.code}</div>}
                </TableCell>
              </TableRow>
            ))}
            {data && !rows.length && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10" data-testid="regulars-empty">No returning guests yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send WhatsApp invite to {selected.size} {selected.size === 1 ? "guest" : "guests"}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {data?.preview ? (
                  <>
                    <p>Template <code>{data.preview.template}</code>:</p>
                    <p className="whitespace-pre-line bg-stone-50 border rounded p-3 text-stone-800" data-testid="regulars-preview-text">{data.preview.text}</p>
                    {!data.preview.personal_link && (
                      <p className="text-amber-700 text-xs">This fallback has no personal link — regulars still get early access by booking with the email or phone of a previous visit. Once "tribe_regulars_invite" is approved, each guest gets their own link.</p>
                    )}
                  </>
                ) : <p className="text-red-600">No approved invite template found in respond.io.</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={send} disabled={sending} data-testid="regulars-confirm-send">Send now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
